// ServiceFlavorsWorkspace.js — top-level Service Flavors builder.
// Derives takeoff service lines via the real engine chain, seeds 3 default
// flavors (Basic / Standard / Premium), prices each through computeFlavor, and
// renders a global controls band + 3 FlavorCards + a per-flavor DeeperTweaksModal.
// All pricing math lives in serviceFlavorsModel / estimateEngine; this file owns
// React state, the derivation memo chain, mutation handlers, and view-models.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

import { calculateLaborHours } from '../../../SiteTakeoff/laborCalculator';
import {
  DEFAULT_ESTIMATE_SETTINGS,
  buildServiceLinesFromTakeoff,
  computeService,
  heatFactorForMonth,
} from '../estimateEngine';
import { getSuggestedServices } from '../../../SiteTakeoff/siteTakeoffConstants';
import { applyRateOverrides } from '../applyRateOverrides';
import {
  computeFlavor,
  seedFromTakeoff,
  roundTo,
  clamp,
  FREQS,
} from './serviceFlavorsModel';
import { buildDefaultFlavors, STARTUP_CATALOG } from './defaultFlavors';
import ScopePanel from '../scope/ScopePanel';
import SettingsModal, { RatesStrip } from '../scope/SettingsModal';
import FlavorCard from './FlavorCard';
import DeeperTweaksModal from './DeeperTweaksModal';
import buildEstimateContract from './buildEstimateContract';
import CalcTracePanel from '../../../SiteTakeoff/CalcTracePanel';
import './serviceFlavors.css';

// ── helpers ────────────────────────────────────────────────────────────────

// rateSeed.rates is keyed by CATEGORY (mtc/arborist/herbicide/irrigation); the
// engine reads settings.laborRates by ROLE (Maint/Arborist/Herbicide/Irrigation).
// Translate explicitly — a naive {...d, ...rates} would never reach the engine.
const seededLaborRates = (rateSeed) => {
  const r = (rateSeed && rateSeed.rates) || {};
  const d = DEFAULT_ESTIMATE_SETTINGS.laborRates;
  return {
    ...d,
    Maint: Number(r.mtc) || d.Maint,
    Arborist: Number(r.arborist) || d.Arborist,
    Herbicide: Number(r.herbicide) || d.Herbicide,
    Irrigation: Number(r.irrigation) || d.Irrigation,
    Operator: Number(r.operator) || d.Operator,
    'Crane Operator': Number(r.craneOperator) || d['Crane Operator'],
  };
};

// Map the engine's modifierTable (string keys) to laborCalculator's modifier
// keys. Mirrors TakeoffEstimateWorkspace so the same real multipliers apply.
const toLaborCalculatorModifiers = (modifierTable = {}) => ({
  access_obstructed: Number(modifierTable.obstructed) || 1,
  slope_sloped: Number(modifierTable.sloped) || 1,
  litterExposure_high: Number(modifierTable['high-litter']) || 1,
  weedPressure_med: Number(modifierTable['med-weed']) || 1,
  weedPressure_high: Number(modifierTable['high-weed']) || 1,
  limitedAccess: Number(modifierTable['limited-access']) || 1,
  blowRestrictions: Number(modifierTable['blow-restrictions']) || 1,
});

const money = (n) => '$' + Math.round(Number(n) || 0).toLocaleString('en-US');
const fmtNumber = (n, d = 1) => (Number(n) || 0).toLocaleString('en-US', {
  minimumFractionDigits: d, maximumFractionDigits: d,
});
const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

// Per-service display metadata. accent feeds the colored dot on each row.
const SERVICE_META = [
  { role: 'Maint', label: 'Maintenance', delivery: 'labor', accent: '#3a5a8c' },
  { role: 'Irrigation', label: 'Irrigation', delivery: 'labor', accent: '#2f7350' },
  { role: 'Herbicide', label: 'Herbicide', delivery: 'flat', accent: '#6b4e9e' },
  { role: 'Arborist', label: 'Arborist', delivery: 'tree', accent: '#8a4a36' },
  { role: 'Fert', label: 'Fertilization', delivery: 'flat', accent: '#9a7d2c' },
];

const fiClamp = (fi) => clamp(typeof fi === 'number' ? fi : 4, 0, FREQS.length - 1);

const cloneEstimateSettings = (rateSeed) => ({
  ...DEFAULT_ESTIMATE_SETTINGS,
  laborRates: seededLaborRates(rateSeed),
  productionRates: JSON.parse(JSON.stringify(DEFAULT_ESTIMATE_SETTINGS.productionRates || {})),
  frequencies: JSON.parse(JSON.stringify(DEFAULT_ESTIMATE_SETTINGS.frequencies || {})),
  modifierTable: { ...(DEFAULT_ESTIMATE_SETTINGS.modifierTable || {}) },
  irrigationZoneInference: { ...(DEFAULT_ESTIMATE_SETTINGS.irrigationZoneInference || {}) },
  subcontractors: JSON.parse(JSON.stringify(DEFAULT_ESTIMATE_SETTINGS.subcontractors || {})),
  seasonalHeatFactor: heatFactorForMonth(new Date().getMonth()),
});

const mergeEstimateSettings = (base, saved = {}) => ({
  ...base,
  ...saved,
  laborRates: { ...(base.laborRates || {}), ...(saved.laborRates || {}) },
  productionRates: { ...(base.productionRates || {}), ...(saved.productionRates || {}) },
  frequencies: { ...(base.frequencies || {}), ...(saved.frequencies || {}) },
  modifierTable: { ...(base.modifierTable || {}), ...(saved.modifierTable || {}) },
  irrigationZoneInference: { ...(base.irrigationZoneInference || {}), ...(saved.irrigationZoneInference || {}) },
  subcontractors: {
    ...(base.subcontractors || {}),
    ...(saved.subcontractors || {}),
    preEmergent: {
      ...(base.subcontractors?.preEmergent || {}),
      ...(saved.subcontractors?.preEmergent || {}),
    },
  },
  seasonalHeatFactor: heatFactorForMonth(new Date().getMonth()),
});

// ── component ──────────────────────────────────────────────────────────────

export default function ServiceFlavorsWorkspace({
  url = '',
  jobsite,
  areas = [],
  takeoff,
  rateSeed,
  onBackToTakeoff,
  onGenerateProposal,
  onOpenProposal,
  onToast, // eslint-disable-line no-unused-vars -- reserved for Phase-2 save
}) {
  // Engine settings: seasonal heat for the current month + seeded labor rates.
  // STATE (not a memo) so the Settings modal can edit production rates / labor /
  // modifiers / subcontractors / frequencies — edits re-derive hours → re-price.
  const [settings, setSettings] = useState(() => cloneEstimateSettings(rateSeed));
  // Re-apply seeded customer labor rates when the rate-seed changes (preserves
  // any other in-modal edits — only laborRates is refreshed from the seed).
  useEffect(() => {
    setSettings((cur) => ({ ...cur, laborRates: seededLaborRates(rateSeed) }));
  }, [rateSeed]);
  const onSettingsChange = (updater) => setSettings(updater);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState('rates');
  const [estimateId, setEstimateId] = useState(null);
  const [existingProposal, setExistingProposal] = useState(null); // { id, status, updatedAt } | null
  const [saveStatus, setSaveStatus] = useState('idle');
  const [generatingProposal, setGeneratingProposal] = useState(false);
  const hydratedDraftRef = useRef(false);
  const saveTimerRef = useRef(null);
  const lastSavedPayloadRef = useRef('');

  // Real derivation chain: areas → labor hours → engine service lines → seed.
  const hoursResult = useMemo(() => calculateLaborHours(areas, {
    ...settings.productionRates,
    _modifiers: toLaborCalculatorModifiers(settings.modifierTable),
  }), [areas, settings]);

  const baseServices = useMemo(
    () => buildServiceLinesFromTakeoff(areas, hoursResult, settings),
    [areas, hoursResult, settings],
  );

  // ── scope state (shared, all flavors inherit) ──
  // enabledByRole overrides a service's enabled flag; rateOverrides edits per-task
  // coverage/frequency. The adjusted `services` feed BOTH the ScopePanel and the
  // flavor pricing, so editing scope re-prices all three flavors live.
  const [enabledByRole, setEnabledByRole] = useState({});
  const [rateOverrides, setRateOverrides] = useState({});

  const services = useMemo(
    () => applyRateOverrides(
      baseServices.map((s) => ({ ...s, enabled: enabledByRole[s.role] ?? s.enabled })),
      rateOverrides,
      settings,
    ),
    [baseServices, enabledByRole, rateOverrides, settings],
  );

  const seed = useMemo(() => seedFromTakeoff(services, areas), [services, areas]);

  // Total annual labor hours across labor services (for the scope header).
  const totalLaborHours = useMemo(
    () => services
      .filter((s) => s.delivery === 'labor' && s.enabled)
      .reduce((sum, s) => sum + computeService(s, settings).hours, 0),
    [services, settings],
  );

  const openSettings = (tab = 'rates') => {
    setSettingsInitialTab(tab);
    setSettingsOpen(true);
  };

  const editAssumptions = (role) => {
    if (role === 'Herbicide') {
      openSettings('subs');
      return;
    }
    if (role === 'Irrigation') {
      openSettings('rates');
      return;
    }
    openSettings('rates');
  };

  // ── state ──
  const [globals, setGlobals] = useState({ margin: 50, driveRate: 45, taxable: false, taxRate: 8.05 });
  const [flavors, setFlavors] = useState(() => buildDefaultFlavors(seed));
  const [modalTier, setModalTier] = useState(null);
  const [flavorsCollapsed, setFlavorsCollapsed] = useState(false);
  // Tier on/off state, keyed by flavor id. Absent/true = enabled. Lets the user
  // narrow down to fewer tiers (even just one) before generating a proposal.
  const [flavorEnabled, setFlavorEnabled] = useState({});

  // ── M6: per-field dirty tracking ─────────────────────────────────────────
  // Takeoff-derived fields that seed into flavor state and must track live
  // takeoff changes UNLESS the user has manually edited them. Fields:
  //   'Arborist.trees'        — from area treeCount (non-palm)
  //   'Arborist.palms'        — from area treeCount where species ~ /palm/i
  //   'Herbicide.perVisitCost'— from herbicide subcontract seed
  //   'Fert.perVisitCost'     — from fert subcontract seed
  // A field is dirty when the user edits it via the UI; the auto-save cycle must
  // never mark fields dirty (seeding from takeoff changes is NOT a user edit).
  // Dirty flags are persisted in the draft payload so they survive hydration.
  const dirtyFieldsRef = useRef(new Set()); // Set<'Arborist.trees' | 'Arborist.palms' | 'Herbicide.perVisitCost' | 'Fert.perVisitCost'>
  const initialSeededRef = useRef(false); // tracks whether we've done the first non-empty seed

  useEffect(() => {
    const takeoffId = takeoff?._id;
    hydratedDraftRef.current = false;
    lastSavedPayloadRef.current = '';
    if (!takeoffId) {
      setEstimateId(null);
      setExistingProposal(null);
      hydratedDraftRef.current = true;
      return undefined;
    }

    let cancelled = false;

    // Fetch the existing proposal summary (parallel to the draft fetch below).
    // Fails silently on 404; logs non-404 errors only.
    axios.get(`${url}/api/proposal/takeoff-estimate/existing-proposal/${takeoffId}`)
      .then((resp) => {
        if (cancelled) return;
        const d = resp.data || {};
        setExistingProposal(d.proposalId ? { id: d.proposalId, status: d.status, updatedAt: d.updatedAt } : null);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status !== 404) console.error('Failed to load existing proposal summary:', err);
        setExistingProposal(null);
      });

    axios.get(`${url}/api/proposal/takeoff-estimate/by-takeoff/${takeoffId}`)
      .then((resp) => {
        if (cancelled) return;
        const draft = resp.data || {};
        setEstimateId(draft._id || null);
        if (draft.settings && typeof draft.settings === 'object') {
          const merged = mergeEstimateSettings(cloneEstimateSettings(rateSeed), draft.settings);
          setSettings(merged);
          const savedFlavorState = draft.settings.serviceFlavors || null;
          if (savedFlavorState && savedFlavorState.version === 1) {
            if (savedFlavorState.globals) setGlobals(savedFlavorState.globals);
            if (Array.isArray(savedFlavorState.flavors) && savedFlavorState.flavors.length) {
              // Restore dirty field flags FIRST so the seed-apply below respects them.
              // Dirty keys are per flavor (`${fid}.Arborist.trees`). Legacy drafts
              // stored bare field names (no fid) that pinned all tiers at once; drop
              // those on restore so those fields fall back to live-seed tracking —
              // the safe default (the user can always re-edit to re-pin per tier).
              if (Array.isArray(savedFlavorState.dirtyFields)) {
                const perFlavorKeys = savedFlavorState.dirtyFields.filter(
                  (k) => typeof k === 'string' && k.split('.').length >= 3,
                );
                dirtyFieldsRef.current = new Set(perFlavorKeys);
              }
              // Hydrate the draft flavors, then immediately apply the LIVE seed for any
              // non-dirty seeded fields. This ensures a takeoff change between the last
              // save and this mount flows through correctly (M6 live-tracking guarantee).
              const liveSeed = seed; // close over the current seed at hydration time
              const dirty = dirtyFieldsRef.current;
              setFlavors(savedFlavorState.flavors.map((f) => {
                let arbSvc = f.svc.Arborist;
                if (!dirty.has(`${f.id}.Arborist.trees`) && liveSeed.Arborist?.trees != null) {
                  arbSvc = { ...arbSvc, trees: liveSeed.Arborist.trees };
                }
                if (!dirty.has(`${f.id}.Arborist.palms`) && liveSeed.Arborist?.palms != null) {
                  arbSvc = { ...arbSvc, palms: liveSeed.Arborist.palms };
                }
                let herbSvc = f.svc.Herbicide;
                if (!dirty.has(`${f.id}.Herbicide.perVisitCost`) && liveSeed.Herbicide?.perVisitCost != null) {
                  herbSvc = { ...herbSvc, perVisitCost: liveSeed.Herbicide.perVisitCost };
                }
                let fertSvc = f.svc.Fert;
                if (!dirty.has(`${f.id}.Fert.perVisitCost`) && liveSeed.Fert?.perVisitCost != null) {
                  fertSvc = { ...fertSvc, perVisitCost: liveSeed.Fert.perVisitCost };
                }
                return { ...f, svc: { ...f.svc, Arborist: arbSvc, Herbicide: herbSvc, Fert: fertSvc } };
              }));
              initialSeededRef.current = true;
            }
            // dirtyFields already restored above when flavors were loaded.
            if (savedFlavorState.flavorEnabled && typeof savedFlavorState.flavorEnabled === 'object') {
              setFlavorEnabled(savedFlavorState.flavorEnabled);
            }
            if (savedFlavorState.enabledByRole && typeof savedFlavorState.enabledByRole === 'object') {
              setEnabledByRole(savedFlavorState.enabledByRole);
            }
            if (savedFlavorState.rateOverrides && typeof savedFlavorState.rateOverrides === 'object') {
              setRateOverrides(savedFlavorState.rateOverrides);
            } else if (draft.rateOverrides && typeof draft.rateOverrides === 'object') {
              setRateOverrides(draft.rateOverrides);
            }
          } else if (draft.rateOverrides && typeof draft.rateOverrides === 'object') {
            setRateOverrides(draft.rateOverrides);
          }
        }
        if (Array.isArray(draft.services) && !draft.settings?.serviceFlavors?.enabledByRole) {
          const enabled = {};
          draft.services.forEach((s) => { if (s && s.role) enabled[s.role] = !!s.enabled; });
          setEnabledByRole(enabled);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status !== 404) console.error('Failed to load service flavor draft:', err);
      })
      .finally(() => {
        if (!cancelled) hydratedDraftRef.current = true;
      });

    return () => {
      cancelled = true;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [takeoff?._id, url, rateSeed]);

  // Re-seed when takeoff seed changes, but only update fields not marked dirty by
  // the user. On first non-empty seed (initialSeededRef=false) all fields are seeded
  // regardless of dirty flags (none exist yet). After that, only clean fields track
  // the takeoff. This means: adding 20 more trees after save re-prices the Arborist
  // line automatically — unless the user has already manually overridden tree count.
  useEffect(() => {
    const hasData = Object.values(seed).some(
      (s) => Number(s?.annualHours) > 0 || Number(s?.trees) > 0 || Number(s?.palms) > 0,
    );
    if (!hasData) return;

    if (!initialSeededRef.current) {
      // First non-empty seed — initialize all flavors from takeoff.
      setFlavors(buildDefaultFlavors(seed));
      initialSeededRef.current = true;
      return;
    }

    // Subsequent seed changes: update only non-dirty seeded fields, per flavor —
    // a field pinned on one tier still tracks the takeoff on the others.
    const dirty = dirtyFieldsRef.current;
    setFlavors((fs) => fs.map((f) => {
      let arbSvc = f.svc.Arborist;
      // Only update counts if the respective field isn't dirty on THIS flavor.
      if (!dirty.has(`${f.id}.Arborist.trees`)) {
        arbSvc = { ...arbSvc, trees: seed.Arborist?.trees ?? arbSvc.trees };
      }
      if (!dirty.has(`${f.id}.Arborist.palms`)) {
        arbSvc = { ...arbSvc, palms: seed.Arborist?.palms ?? arbSvc.palms };
      }
      let herbSvc = f.svc.Herbicide;
      if (!dirty.has(`${f.id}.Herbicide.perVisitCost`) && seed.Herbicide?.perVisitCost != null) {
        herbSvc = { ...herbSvc, perVisitCost: seed.Herbicide.perVisitCost };
      }
      let fertSvc = f.svc.Fert;
      if (!dirty.has(`${f.id}.Fert.perVisitCost`) && seed.Fert?.perVisitCost != null) {
        fertSvc = { ...fertSvc, perVisitCost: seed.Fert.perVisitCost };
      }
      if (arbSvc === f.svc.Arborist && herbSvc === f.svc.Herbicide && fertSvc === f.svc.Fert) {
        return f;
      }
      return { ...f, svc: { ...f.svc, Arborist: arbSvc, Herbicide: herbSvc, Fert: fertSvc } };
    }));
  }, [seed]); // dirtyFieldsRef is intentionally not a dep (it's a ref, not state)

  // Total mapped square footage: sum parent areas minus any exclusion-child SF.
  // Mirrors the laborCalculator netting so the contract site.sqft is workable SF,
  // not gross SF that includes building footprints / pool cutouts inside parent areas.
  const totalSF = useMemo(() => {
    const exclusionSFByParent = {};
    areas.forEach((a) => {
      if (!a.parentAreaId) return;
      const pid = typeof a.parentAreaId === 'object' ? a.parentAreaId.toString() : String(a.parentAreaId);
      exclusionSFByParent[pid] = (exclusionSFByParent[pid] || 0) + (Number(a.sqft ?? a.area) || 0);
    });
    return areas
      .filter((a) => !a.parentAreaId)
      .reduce((s, a) => {
        const rawId = a._id || a.id;
        const id = rawId ? (typeof rawId === 'object' ? rawId.toString() : String(rawId)) : null;
        const gross = Number(a.sqft ?? a.area) || 0;
        const excSF = id ? (exclusionSFByParent[id] || 0) : 0;
        return s + Math.max(0, gross - excSF);
      }, 0);
  }, [areas]);
  const areaCount = areas.filter((a) => !a.parentAreaId).length;

  // M4: SF that prices nothing — parent areas whose ACTUAL servicesRequired is
  // empty (user stripped them, or a zero-service type like ELLIPSE_ZONE /
  // unclassified supplied no defaults). Areas without a servicesRequired field
  // fall back to their type defaults; pre-emergent-only areas still price the
  // per-acre sub, so they don't warn.
  const unmapped = useMemo(() => {
    let sf = 0;
    let count = 0;
    areas.forEach((a) => {
      if (a.parentAreaId) return; // exclusion children don't price on their own
      if (a.preEmergentEnabled) return;
      const services = Array.isArray(a.servicesRequired)
        ? a.servicesRequired
        : getSuggestedServices(a.areaType);
      if (services.length === 0) {
        sf += Number(a.sqft ?? a.area) || 0;
        count += 1;
      }
    });
    return { sf, count };
  }, [areas]);

  // ── scope mutations (shared base scope) ──
  // Per-task coverage/frequency edit → rateOverrides[serviceCode]; recomputed by
  // applyRateOverrides into `services`, which re-prices every flavor.
  const handleRateChange = (serviceCode, patch) => {
    if (!serviceCode) return;
    setRateOverrides((cur) => ({ ...cur, [serviceCode]: { ...cur[serviceCode], ...patch } }));
  };
  const toggleRole = (role) => setEnabledByRole((cur) => {
    const current = cur[role] ?? (baseServices.find((s) => s.role === role)?.enabled ?? false);
    return { ...cur, [role]: !current };
  });

  // ── tier on/off ──
  const enabledFlavors = flavors.filter((f) => flavorEnabled[f.id] !== false);
  // Toggling the last remaining enabled tier off is a no-op — at least one
  // tier must stay enabled so there's always something to price/present.
  const toggleFlavorEnabled = (fid) => {
    const currentlyOn = flavorEnabled[fid] !== false;
    if (currentlyOn && enabledFlavors.length <= 1) return;
    setFlavorEnabled((cur) => ({ ...cur, [fid]: !currentlyOn }));
  };

  // ── mutations (pure setState updaters) ──
  const patchSvc = (fid, role, fn) => setFlavors((fs) => fs.map(
    (f) => (f.id === fid ? { ...f, svc: { ...f.svc, [role]: fn(f.svc[role]) } } : f),
  ));
  const patchFlavor = (fid, fn) => setFlavors((fs) => fs.map((f) => (f.id === fid ? fn(f) : f)));

  const cycleMode = (fid, role) => {
    const next = { in: 'extra', extra: 'off', off: 'in' };
    patchSvc(fid, role, (st) => ({ ...st, mode: next[st.mode] }));
  };
  const stepFreq = (fid, role, d) => patchSvc(fid, role, (st) => ({
    ...st, fi: clamp((st.fi ?? 4) + d, 0, FREQS.length - 1),
  }));
  // Arborist dial edits the tree count; other count/flat roles are frequency-driven.
  // Labor roles (Maint/Irrigation) use a per-flavor hours % adjuster (50–150).
  const stepDur = (fid, role, d) => {
    // Arborist tree-count stepper: mark dirty so the takeoff seed stops updating.
    // Keyed per flavor (`${fid}.Arborist.trees`) so editing one tier never freezes
    // the seeded count on the other tiers.
    if (role === 'Arborist') {
      dirtyFieldsRef.current = new Set([...dirtyFieldsRef.current, `${fid}.Arborist.trees`]);
    }
    patchSvc(fid, role, (st) => {
      if (role === 'Arborist') return { ...st, trees: Math.max(0, (st.trees || 0) + d) };
      return { ...st, hoursPct: clamp((st.hoursPct ?? 100) + d * 5, 50, 150) };
    });
  };
  const setPerVisitCost = (fid, role, value) => {
    // Mark Herbicide and Fert perVisitCost as dirty — they are seeded from the
    // takeoff and must stop auto-updating once the user explicitly sets a value.
    // Per-flavor keyed so a per-tier edit doesn't pin the field on other tiers.
    if (role === 'Herbicide' || role === 'Fert') {
      dirtyFieldsRef.current = new Set([...dirtyFieldsRef.current, `${fid}.${role}.perVisitCost`]);
    }
    patchSvc(fid, role, (st) => ({
      ...st,
      perVisitCost: Math.max(0, toNumber(value, 0)),
    }));
  };
  // Typed overrides for the Arborist counts (steppers stay for quick nudges).
  // Both mark the respective field dirty (per flavor) so the takeoff seed stops
  // updating it on that tier only.
  const setTreeCount = (fid, value) => {
    dirtyFieldsRef.current = new Set([...dirtyFieldsRef.current, `${fid}.Arborist.trees`]);
    patchSvc(fid, 'Arborist', (st) => ({
      ...st, trees: Math.max(0, Math.round(toNumber(value, 0))),
    }));
  };
  const setPalmCount = (fid, value) => {
    dirtyFieldsRef.current = new Set([...dirtyFieldsRef.current, `${fid}.Arborist.palms`]);
    patchSvc(fid, 'Arborist', (st) => ({
      ...st, palms: Math.max(0, Math.round(toNumber(value, 0))),
    }));
  };
  // Single-select: promoting one tier demotes the others; clicking the active
  // badge clears the recommendation entirely (proposal just renders no ribbon).
  const setRecommended = (fid) => setFlavors((fs) => fs.map((f) => ({
    ...f, recommended: f.id === fid ? !f.recommended : false,
  })));

  const setMargin = (v) => setGlobals((g) => ({ ...g, margin: clamp(Number(v) || 0, 20, 95) }));
  const toggleTax = () => setGlobals((g) => ({ ...g, taxable: !g.taxable }));
  const stepTax = (d) => setGlobals((g) => ({ ...g, taxRate: clamp(Math.round((g.taxRate + d) * 100) / 100, 0, 15) }));

  // modal mutations
  const setDays = (fid, n) => patchSvc(fid, 'Maint', (st) => ({ ...st, days: n }));
  const stepBlow = (fid, d) => patchSvc(fid, 'Maint', (st) => ({ ...st, blowGo: clamp((st.blowGo || 0) + d, 0, 104) }));
  const setPrune = (fid, v) => patchSvc(fid, 'Maint', (st) => ({ ...st, prune: v }));
  const setCov = (fid, i) => patchSvc(fid, 'Irrigation', (st) => ({ ...st, cov: i }));
  const toggleStartup = (fid, key) => patchFlavor(fid, (f) => ({
    ...f, startup: { ...f.startup, [key]: { ...f.startup[key], on: !f.startup[key].on } },
  }));
  const stepStartup = (fid, key, d) => patchFlavor(fid, (f) => ({
    ...f, startup: { ...f.startup, [key]: { ...f.startup[key], cost: clamp((f.startup[key].cost || 0) + d * 100, 0, 50000) } },
  }));
  const setStartupCost = (fid, key, value) => patchFlavor(fid, (f) => ({
    ...f,
    startup: {
      ...f.startup,
      [key]: {
        ...f.startup[key],
        cost: clamp(toNumber(value, 0), 0, 50000),
      },
    },
  }));

  // ── pricing + view-models ──
  const { vms, chips, pricedById } = useMemo(() => {
    // Price every flavor (not just enabled ones) so pricedById stays a complete
    // lookup — the deeper-tweaks modal keys off it by id regardless of a tier's
    // on/off state.
    const pricedAll = flavors.map((f) => ({ f, c: computeFlavor(f, globals, settings, services) }));
    const priced = pricedAll.filter(({ f }) => flavorEnabled[f.id] !== false);
    // Baseline is Standard when present, else whatever tier the fallback picks —
    // compare every card's id against THAT resolved tier's id (not the literal
    // string 'standard') so disabling Standard itself doesn't leave the actual
    // baseline card mislabeled with a "+$0" delta.
    const std = priced.find((p) => p.f.id === 'standard') || priced[1] || priced[0];
    const stdAnnualR = std ? roundTo(std.c.monthly, 5) * 12 : 0;

    const builtVms = priced.map(({ f, c }) => {
      const monthlyR = roundTo(c.monthly, 5);
      const annualR = monthlyR * 12;
      const isBase = !!std && f.id === std.f.id;
      const d = annualR - stdAnnualR;
      const deltaStr = isBase ? 'Baseline' : (d >= 0 ? '+' : '−') + money(Math.abs(d));
      const deltaDir = isBase ? 'base' : (d >= 0 ? 'up' : 'down');

      // extras by role → quick lookup for service feeStr + add-ons list
      const extraByRole = {};
      (c.extras || []).forEach((e) => { extraByRole[e.role] = e; });

      const services = SERVICE_META.map((meta) => {
        const st = f.svc[meta.role] || { mode: 'off' };
        const role = meta.role;
        const isMaint = role === 'Maint';
        const isTree = meta.delivery === 'tree';
        const isFlat = meta.delivery === 'flat';
        const isLaborRole = role === 'Maint' || role === 'Irrigation';

        // total string by mode — labor roles show REAL hrs/yr from the engine.
        let totalStr = '—';
        if (st.mode === 'extra') {
          const ex = extraByRole[role];
          totalStr = ex ? `+${money(roundTo(ex.feeMonthly, 5))}/mo` : '—';
        } else if (st.mode === 'in') {
          if (isLaborRole) {
            totalStr = `${fmtNumber(c.hoursByRole?.[role] || 0, 1)} hrs/yr`;
          } else if (isTree) {
            totalStr = `${st.trees || 0} trees · ${st.palms || 0} palms`;
          } else if (isFlat) {
            totalStr = `${FREQS[fiClamp(st.fi)].v} visits`;
          }
        }

        // freq + duration labels (ServiceRow falls back to its own if omitted)
        const freqLabel = isMaint
          ? `${st.days}×/wk`
          : (isFlat || isTree)
            ? `${FREQS[fiClamp(st.fi)].v} visits`
            : FREQS[fiClamp(st.fi)].f;
        const durLabel = isTree
          ? `${st.trees || 0} trees · ${st.palms || 0} palms`
          : isFlat
            ? `${money(st.perVisitCost || 0)}`
            : `${st.hoursPct ?? 100}%`;
        const durCaption = isTree ? 'Trees · palms' : isFlat ? 'Per visit' : 'Hours %';

        const feeStr = st.mode === 'extra' && extraByRole[role]
          ? `${money(roundTo(extraByRole[role].feeMonthly, 5))}/mo`
          : undefined;

        return { meta, st, totalStr, freqLabel, durLabel, durCaption, feeStr };
      });

      const extras = (c.extras || []).map((e) => {
        const m = SERVICE_META.find((mm) => mm.role === e.role);
        return {
          label: m ? m.label : e.role,
          accent: m ? m.accent : undefined,
          feeStr: '+' + money(roundTo(e.feeMonthly, 5)) + '/mo',
        };
      });

      return {
        id: f.id,
        name: f.name,
        recommended: f.recommended,
        priceStr: money(annualR),
        monthlyStr: money(monthlyR) + ' /mo',
        costStr: money(c.cost),
        sfStr: '$' + (totalSF > 0 ? (annualR / totalSF).toFixed(2) : '0.00'),
        taxable: globals.taxable,
        taxRateStr: globals.taxRate.toFixed(2) + '%',
        withTaxStr: money(monthlyR * (1 + globals.taxRate / 100)) + ' /mo',
        hasStartup: c.startupPrice > 0,
        startupStr: money(c.startupPrice),
        startupItems: STARTUP_CATALOG
          .filter((it) => f.startup?.[it.key]?.on)
          .map((it) => it.label),
        deltaStr,
        deltaDir,
        services,
        extras,
        hasExtras: extras.length > 0,
      };
    });

    const chipData = builtVms.map((vm) => ({
      id: vm.id,
      name: vm.name,
      priceStr: vm.priceStr,
      deltaStr: vm.deltaStr,
      recommended: vm.recommended,
    }));

    const byId = {};
    pricedAll.forEach(({ f, c }) => { byId[f.id] = c; });

    return { vms: builtVms, chips: chipData, pricedById: byId };
  }, [flavors, flavorEnabled, globals, settings, services, totalSF]);

  // active modal flavor + its priced result
  const modalFlavor = modalTier ? flavors.find((f) => f.id === modalTier) : null;
  const modalPriced = modalTier ? pricedById[modalTier] : null;

  // "Show the math" trace for the baseline tier the cards compare against —
  // Standard when present+enabled, else the same fallback the pricing memo uses.
  // pricedById carries each flavor's per-line trace (labor/sub + flavor-local).
  const baselineFlavorId = (
    enabledFlavors.find((f) => f.id === 'standard') || enabledFlavors[1] || enabledFlavors[0]
  )?.id;
  const baselineTrace = baselineFlavorId ? pricedById[baselineFlavorId]?.trace : null;

  const lowMargin = globals.margin < 45;
  const canGenerate = !!onGenerateProposal && enabledFlavors.length > 0 && !generatingProposal;

  const buildCurrentEstimateContract = () => buildEstimateContract({
    flavors: enabledFlavors,
    pricedById,
    globals,
    settings,
    totalSF,
    areaCount,
  });

  const buildSavePayload = () => {
    const estimateContract = buildCurrentEstimateContract();
    const savedSettings = {
      ...settings,
      serviceFlavors: {
        version: 1,
        globals,
        flavors,
        flavorEnabled,
        enabledByRole,
        rateOverrides,
        // Persist dirty fields so user edits survive the draft save/hydrate cycle.
        // The Set is serialized as a sorted array for stable JSON comparison.
        dirtyFields: Array.from(dirtyFieldsRef.current).sort(),
        contract: estimateContract,
      },
    };
    return {
      takeoffId: takeoff?._id,
      jobsiteId: jobsite?._id || takeoff?.jobSite || takeoff?.jobsiteId || null,
      services,
      tiers: estimateContract.flavors.map((flavor) => ({
        id: flavor.id,
        name: flavor.name,
        recommended: !!flavor.recommended,
        marginPct: globals.margin,
        includedServices: (estimateContract.services || [])
          .filter(service => service.byFlavor?.[flavor.id]?.mode === 'included')
          .map(service => service.role),
        targetPrice: { amount: flavor.monthly, basis: 'monthly' },
        annualPrice: flavor.annual,
        monthlyPrice: flavor.monthly,
        perVisitPrice: 0,
        totalCost: pricedById[flavor.id]?.cost || 0,
      })),
      rateOverrides,
      settings: savedSettings,
      status: 'draft',
    };
  };

  const saveFlavorDraft = async ({ silent = true } = {}) => {
    if (!takeoff?._id) {
      if (!silent && onToast) onToast('Save the takeoff before saving estimate tweaks');
      return null;
    }
    const payload = buildSavePayload();
    const payloadKey = JSON.stringify(payload);
    if (silent && payloadKey === lastSavedPayloadRef.current) return estimateId;
    try {
      if (!silent) setSaveStatus('saving');
      const resp = await axios.post(`${url}/api/proposal/takeoff-estimate/save`, payload);
      setEstimateId(resp.data?._id || null);
      lastSavedPayloadRef.current = payloadKey;
      setSaveStatus('saved');
      return resp.data?._id || null;
    } catch (err) {
      console.error('Failed to save service flavor draft:', err);
      setSaveStatus('error');
      if (!silent && onToast) onToast('Could not save estimate tweaks');
      return null;
    }
  };

  useEffect(() => {
    if (!takeoff?._id || !hydratedDraftRef.current) return undefined;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveStatus('dirty');
    saveTimerRef.current = setTimeout(() => {
      saveFlavorDraft({ silent: true });
    }, 900);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [takeoff?._id, jobsite?._id, services, settings, rateOverrides, enabledByRole, flavors, flavorEnabled, globals, pricedById, totalSF, areaCount]);

  // TRAP: never use onClick={handleGenerateProposal} — the click event would be
  // passed as `force` (truthy), making every Update behave like forceNew.
  // Always use explicit arrow wrappers: onClick={() => handleGenerateProposal()}
  const handleGenerateProposal = async (force = false) => {
    if (!canGenerate) return;
    setGeneratingProposal(true);
    try {
      const savedEstimateId = await saveFlavorDraft({ silent: false });
      const estimateContract = buildCurrentEstimateContract();
      const payload = {
        contract: estimateContract,
        estimateId: savedEstimateId || estimateId || null,
        jobsiteId: jobsite?._id || takeoff?.jobSite || takeoff?.jobsiteId || null,
      };
      if (force) payload.forceNew = true;
      const result = await onGenerateProposal(payload);
      if (result?.proposalId) {
        setExistingProposal({
          id: result.proposalId,
          status: result.status || existingProposal?.status || 'draft',
          updatedAt: result.updatedAt || new Date().toISOString(),
        });
      }
    } finally {
      setGeneratingProposal(false);
    }
  };

  const handleOpenProposal = () => {
    if (!generatingProposal && existingProposal?.id && onOpenProposal) onOpenProposal(existingProposal.id);
  };

  return (
    <div className="service-flavors" style={{ padding: 24 }}>
      {/* Title row */}
      <div
        className="sf-title-row"
        style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}
      >
        <div style={{ minWidth: 0 }}>
          {onBackToTakeoff ? (
            <button
              type="button"
              className="sf-back"
              onClick={onBackToTakeoff}
              style={{
                border: 'none', background: 'transparent', cursor: 'pointer',
                color: 'var(--sf-muted)', fontSize: 12.5, padding: 0, marginBottom: 6,
              }}
            >
              ← Back to takeoff
            </button>
          ) : null}
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--sf-ink)' }}>
            Build estimate — service flavors
          </h1>
          <div style={{ fontSize: 13, color: 'var(--sf-muted)', marginTop: 4 }}>
            Ready-to-pitch tiers. Tune the dials, watch the price move.
          </div>
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--sf-ink-2)' }}>
            {jobsite?.name || takeoff?.name || 'Untitled site'}
          </div>
          <div className="num" style={{ fontSize: 12, color: 'var(--sf-muted)' }}>
            {totalSF.toLocaleString()} SF · {areaCount} areas
          </div>
        </div>
        <div className="num" style={{ fontSize: 11.5, color: saveStatus === 'error' ? 'var(--sf-warn)' : 'var(--sf-muted)' }}>
          {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'dirty' ? 'Unsaved changes' : saveStatus === 'saved' ? 'Saved' : saveStatus === 'error' ? 'Save failed' : ''}
        </div>
      </div>

      {/* M4: Sanity warning banner — zero-service SF alert */}
      {unmapped.sf > 0 ? (
        <div
          role="alert"
          className="sf-sanity-warning"
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '7px 14px', marginBottom: 10, borderRadius: 8,
            background: 'var(--sf-warn-bg, #fff8e1)',
            border: '1px solid var(--sf-warn, #f5a623)',
          }}
        >
          <span style={{ fontSize: 14 }}>⚠</span>
          <span style={{ fontSize: 12.5, color: 'var(--sf-warn-ink, #7a4a00)', fontWeight: 600 }}>
            {`${Math.round(unmapped.sf).toLocaleString()} SF across ${unmapped.count} area(s) has no services assigned — edit the area type before generating a proposal.`}
          </span>
        </div>
      ) : null}

      {/* Toolbar: collapse toggle + Settings */}
      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
        <button
          type="button"
          className="sf-settings-btn"
          onClick={() => setSettingsOpen(true)}
          style={{
            border: '1px solid var(--sf-line-2)', background: 'var(--sf-surface)',
            borderRadius: 8, cursor: 'pointer', padding: '5px 12px',
            fontSize: 12, fontWeight: 700, color: 'var(--sf-ink-2)',
          }}
        >
          ⚙ Settings
        </button>
        {existingProposal?.id ? (
          <>
            <button
              type="button"
              className="sf-settings-btn"
              onClick={() => handleGenerateProposal()}
              disabled={!canGenerate}
              title={`Updates the existing proposal${existingProposal.updatedAt && !isNaN(new Date(existingProposal.updatedAt)) ? ` (last updated ${new Date(existingProposal.updatedAt).toLocaleDateString()})` : ''}`}
              style={{
                border: '1px solid var(--sf-green)', background: 'var(--sf-green)',
                borderRadius: 8, cursor: canGenerate ? 'pointer' : 'default',
                padding: '5px 12px', fontSize: 12, fontWeight: 700,
                color: '#fff', opacity: canGenerate ? 1 : 0.5,
              }}
            >
              Update proposal
            </button>
            <button
              type="button"
              className="sf-settings-btn"
              onClick={() => handleOpenProposal()}
              disabled={generatingProposal}
              style={{
                border: '1px solid var(--sf-line-2)', background: 'var(--sf-surface)',
                borderRadius: 8, cursor: generatingProposal ? 'default' : 'pointer', padding: '5px 12px',
                fontSize: 12, fontWeight: 700, color: 'var(--sf-ink-2)', opacity: generatingProposal ? 0.5 : 1,
              }}
            >
              Open
            </button>
            <button
              type="button"
              className="sf-settings-btn"
              onClick={() => handleGenerateProposal(true)}
              disabled={!canGenerate}
              style={{
                border: '1px solid var(--sf-line-2)', background: 'var(--sf-surface)',
                borderRadius: 8, cursor: canGenerate ? 'pointer' : 'default',
                padding: '5px 12px', fontSize: 12, fontWeight: 700,
                color: 'var(--sf-ink-2)', opacity: canGenerate ? 1 : 0.5,
              }}
            >
              New proposal
            </button>
          </>
        ) : (
          <button
            type="button"
            className="sf-settings-btn"
            onClick={() => handleGenerateProposal()}
            disabled={!canGenerate}
            style={{
              border: '1px solid var(--sf-green)', background: 'var(--sf-green)',
              borderRadius: 8, cursor: canGenerate ? 'pointer' : 'default',
              padding: '5px 12px', fontSize: 12, fontWeight: 700,
              color: '#fff', opacity: canGenerate ? 1 : 0.5,
            }}
          >
            Create proposal
          </button>
        )}
        <button
          type="button"
          className="sf-collapse-toggle"
          onClick={() => setFlavorsCollapsed((c) => !c)}
          style={{
            border: '1px solid var(--sf-line-2)', background: 'var(--sf-surface)',
            borderRadius: 8, cursor: 'pointer', padding: '5px 12px',
            fontSize: 12, fontWeight: 700, color: 'var(--sf-ink-2)',
          }}
        >
          {flavorsCollapsed ? 'Show flavors ▸' : 'Hide flavors ◂'}
        </button>
        <div className="sf-tier-toggles" style={{ display: 'flex', gap: 6, marginLeft: 4 }}>
          {flavors.map((f) => {
            const on = flavorEnabled[f.id] !== false;
            return (
              <button
                key={f.id}
                type="button"
                aria-pressed={on}
                aria-label={`${f.name} tier`}
                onClick={() => toggleFlavorEnabled(f.id)}
                className="sf-tier-toggle"
                style={{
                  border: '1px solid var(--sf-line-2)', borderRadius: 999, cursor: 'pointer',
                  padding: '3px 10px', fontSize: 11.5, fontWeight: 700,
                  color: on ? '#fff' : 'var(--sf-muted)',
                  background: on ? 'var(--sf-green)' : 'var(--sf-surface)',
                }}
              >
                {on ? '☑' : '☐'} {f.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Two-column layout: scope (left) + flavors (right) */}
      <div className={`sf-estimate-layout${flavorsCollapsed ? ' is-collapsed' : ''}`}>
        {/* LEFT — takeoff scope & frequency (shared base scope) */}
        <div className="sf-scope-col">
          <ScopePanel
            services={services}
            settings={settings}
            onToggleRole={toggleRole}
            onRateChange={handleRateChange}
            onEditAssumptions={editAssumptions}
            totalLaborHours={totalLaborHours}
          />
        </div>

        {/* RIGHT — flavors */}
        <div className={`sf-flavors-col${flavorsCollapsed ? ' is-collapsed' : ''}`}>

      {/* Global controls band */}
      <div
        className="sf-band"
        style={{
          display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
          padding: '14px 18px', marginBottom: 18,
        }}
      >
        {/* Margin slider */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 220 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, letterSpacing: '.05em', textTransform: 'uppercase',
              color: 'var(--sf-muted)', fontWeight: 700 }}>
              Target margin
            </span>
            <span className="num" style={{ fontSize: 14, fontWeight: 700, color: 'var(--sf-ink-2)' }}>
              {globals.margin}%
            </span>
            {lowMargin ? (
              <span
                className="sf-band-warn"
                style={{
                  fontSize: 10.5, fontWeight: 700, color: '#fff', background: 'var(--sf-warn)',
                  borderRadius: 999, padding: '2px 8px',
                }}
              >
                Below 45%
              </span>
            ) : null}
          </div>
          <input
            type="range"
            min="20"
            max="95"
            step="1"
            value={globals.margin}
            onChange={(e) => setMargin(e.target.value)}
            aria-label="Target margin"
            style={{ width: '100%' }}
          />
        </div>

        {/* Drive-time stepper hidden this pass (drive-time deferred). */}

        {/* Taxable toggle + rate stepper */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 11, letterSpacing: '.05em', textTransform: 'uppercase',
            color: 'var(--sf-muted)', fontWeight: 700 }}>
            Sales tax
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={toggleTax}
              className="sf-band-toggle"
              aria-pressed={globals.taxable}
              style={{
                border: '1px solid var(--sf-line-2)', borderRadius: 999, cursor: 'pointer',
                padding: '3px 12px', fontSize: 11.5, fontWeight: 700,
                color: globals.taxable ? '#fff' : 'var(--sf-muted)',
                background: globals.taxable ? 'var(--sf-green)' : 'var(--sf-surface)',
              }}
            >
              {globals.taxable ? 'Taxable' : 'No tax'}
            </button>
            <button type="button" aria-label="decrease tax rate" onClick={() => stepTax(-0.25)} className="sf-band-step">−</button>
            <span className="num" style={{ minWidth: 48, textAlign: 'center', fontWeight: 600, color: 'var(--sf-ink-2)' }}>
              {globals.taxRate.toFixed(2)}%
            </span>
            <button type="button" aria-label="increase tax rate" onClick={() => stepTax(0.25)} className="sf-band-step">+</button>
          </div>
        </div>

        {/* Price chips */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
          {chips.map((chip) => (
            <div
              key={chip.id}
              className="sf-band-chip"
              style={{
                display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 12px',
                borderRadius: 10, border: '1px solid var(--sf-line)', background: 'var(--sf-inset)',
                minWidth: 96,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {chip.recommended ? (
                  <span
                    className="sf-band-chip-dot"
                    style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--sf-green)' }}
                  />
                ) : null}
                <span style={{ fontSize: 10.5, letterSpacing: '.05em', textTransform: 'uppercase',
                  color: 'var(--sf-muted)', fontWeight: 700 }}>
                  {`${chip.name} tier`}
                </span>
              </div>
              <span className="num" style={{ fontSize: 15, fontWeight: 700, color: 'var(--sf-ink)' }}>
                {chip.priceStr}
              </span>
              <span className="num" style={{ fontSize: 11, color: 'var(--sf-muted)' }}>{chip.deltaStr}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Cards */}
      <div className="sf-cards">
        {vms.map((vm) => (
          <FlavorCard
            key={vm.id}
            vm={vm}
            onOpenModal={() => setModalTier(vm.id)}
            onCycleMode={cycleMode}
            onStepFreq={stepFreq}
            onStepDur={stepDur}
            onSetPerVisitCost={setPerVisitCost}
            onSetTrees={setTreeCount}
            onSetPalms={setPalmCount}
            onSetRecommended={setRecommended}
          />
        ))}
      </div>

      {/* Rates summary + open-settings strip */}
      <div className="sf-rates-strip" style={{ marginTop: 14 }}>
        <RatesStrip services={services} settings={settings} onSettingsClick={() => openSettings('rates')} />
      </div>

      {/* "Show the math" — per-role cost ledger for the baseline (Standard) tier */}
      <CalcTracePanel trace={baselineTrace} />
        </div>
        {/* /sf-flavors-col */}
      </div>
      {/* /sf-estimate-layout */}

      {/* Estimate settings modal */}
      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
        onSettingsChange={onSettingsChange}
        url={url}
        initialTab={settingsInitialTab}
      />

      {/* Deeper-tweaks modal */}
      {modalFlavor && modalPriced ? (
        <DeeperTweaksModal
          flavor={modalFlavor}
          startupStr={money(modalPriced.startupPrice)}
          startupCatalog={STARTUP_CATALOG}
          onClose={() => setModalTier(null)}
          onSetDays={(n) => setDays(modalTier, n)}
          onStepBlow={(d) => stepBlow(modalTier, d)}
          onSetPrune={(v) => setPrune(modalTier, v)}
          onSetCov={(i) => setCov(modalTier, i)}
          onStepServiceFreq={(role, d) => stepFreq(modalTier, role, d)}
          onStepServiceHours={(role, d) => stepDur(modalTier, role, d)}
          onSetPerVisitCost={(role, value) => setPerVisitCost(modalTier, role, value)}
          onToggleStartup={(k) => toggleStartup(modalTier, k)}
          onStepStartup={(k, d) => stepStartup(modalTier, k, d)}
          onSetStartupCost={(k, value) => setStartupCost(modalTier, k, value)}
        />
      ) : null}
    </div>
  );
}
